/**
 * Native RudderStack client.
 *
 * RudderStack is the single event collector (front + server). PostHog is only a
 * destination configured in RudderStack. The SDK is loaded:
 *   - lazily, after first user engagement or when idle (PageSpeed-safe), and
 *   - only once analytics consent is granted via Axeptio.
 *
 * On SDK ready, sends a standard `page` event (page view) for the current URL,
 * then `identify` when `__AUTH_USER__` is present.
 *
 * Public API (`trackEvent`, `identifyUser`, `pageView`) is safe to call at any
 * time: calls are queued until the SDK is loaded, ready and consented.
 */
type RudderAnalytics = import("@rudderstack/analytics-js").RudderAnalytics;
type ApiObject = import("@rudderstack/analytics-js").ApiObject;
type IdentifyTraits = import("@rudderstack/analytics-js").IdentifyTraits;

interface RudderStackConfig {
    enabled: boolean;
    writeKey: string;
    dataPlaneUrl: string;
}

// Axeptio vendor keys that gate analytics. Must match the Axeptio configuration
// (cookies version `joinsteer-fr-EU`). PostHog is the historical vendor key and
// RudderStack forwards to it, so either grants analytics consent.
const ANALYTICS_CONSENT_VENDORS = ["posthog", "rudderstack"];

const ENGAGEMENT_EVENTS = [
    "scroll",
    "click",
    "keydown",
    "touchstart",
    "pointerdown",
] as const;

let analytics: RudderAnalytics | null = null;
let loadStarted = false;
let isReady = false;
let initialised = false;
const pendingCalls: Array<(rs: RudderAnalytics) => void> = [];

function getConfig(): RudderStackConfig | null {
    if (typeof window === "undefined") {
        return null;
    }

    const config = window.__RUDDERSTACK__;
    if (!config || !config.enabled || !config.writeKey || !config.dataPlaneUrl) {
        return null;
    }

    return config;
}

function readCookie(name: string): string | null {
    if (typeof document === "undefined") {
        return null;
    }

    const match = document.cookie.match(
        new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"),
    );

    return match ? decodeURIComponent(match[1]) : null;
}

type AxeptioCookiesPayload = {
    "$$completed"?: boolean;
    "$$googleConsentMode"?: {
        analytics_storage?: string;
    };
    [vendor: string]: unknown;
};

function parseAxeptioCookies(): AxeptioCookiesPayload | null {
    const raw = readCookie("axeptio_cookies");
    if (raw === null) {
        return null;
    }

    try {
        return JSON.parse(raw) as AxeptioCookiesPayload;
    } catch {
        return null;
    }
}

/** Legacy vendor list cookie (`axeptio_authorized_vendors=,posthog,…`). */
function hasAnalyticsConsentFromVendorList(): boolean {
    const authorized = readCookie("axeptio_authorized_vendors");
    if (authorized === null) {
        return false;
    }

    const granted = authorized.split(",").filter(Boolean);

    return ANALYTICS_CONSENT_VENDORS.some((vendor) => granted.includes(vendor));
}

/**
 * Axeptio v2 stores consent in `axeptio_cookies` (Google Consent Mode). The legacy
 * `axeptio_authorized_vendors` list is often empty even when analytics is granted.
 */
function hasAnalyticsConsentFromAxeptioCookies(): boolean {
    const payload = parseAxeptioCookies();
    if (payload === null) {
        return false;
    }

    if (payload["$$googleConsentMode"]?.analytics_storage === "granted") {
        return true;
    }

    if (payload["$$completed"] === true) {
        return ANALYTICS_CONSENT_VENDORS.some((vendor) => payload[vendor] === true);
    }

    return false;
}

/** Read Axeptio consent from cookies (vendor list or Google Consent Mode). */
function hasAnalyticsConsentFromCookie(): boolean {
    return hasAnalyticsConsentFromVendorList() || hasAnalyticsConsentFromAxeptioCookies();
}

function hasAnalyticsConsentFromChoices(choices: Record<string, unknown>): boolean {
    if (ANALYTICS_CONSENT_VENDORS.some((vendor) => choices[vendor] === true)) {
        return true;
    }

    const gcm = choices["$$googleConsentMode"] as { analytics_storage?: string } | undefined;
    if (gcm?.analytics_storage === "granted") {
        return true;
    }

    return choices.analytics_storage === "granted";
}

function flushPending(): void {
    if (!isReady || analytics === null) {
        return;
    }

    while (pendingCalls.length > 0) {
        const call = pendingCalls.shift();
        call?.(analytics);
    }
}

function enqueue(call: (rs: RudderAnalytics) => void): void {
    pendingCalls.push(call);
    flushPending();
}

function identifyFromAuthUser(): void {
    const user = typeof window !== "undefined" ? window.__AUTH_USER__ : undefined;
    if (!user || !user.email) {
        return;
    }

    identifyUser(user.email, {
        email: user.email,
        name: user.name ?? user.firstname,
    });
}

async function loadSdk(options: { consentGranted?: boolean } = {}): Promise<void> {
    if (loadStarted) {
        return;
    }

    const config = getConfig();
    if (config === null) {
        return;
    }

    if (!options.consentGranted && !hasAnalyticsConsentFromCookie()) {
        return;
    }

    loadStarted = true;

    try {
        const { RudderAnalytics } = await import("@rudderstack/analytics-js");
        analytics = new RudderAnalytics();
        analytics.load(config.writeKey, config.dataPlaneUrl, {
            // Events only: no session recording / session auto-tracking.
            sessions: { autoTrack: false },
            useBeacon: true,
        });
        analytics.ready(() => {
            isReady = true;
            identifyFromAuthUser();
            pageView();
            flushPending();
        });
    } catch (error) {
        loadStarted = false;
        console.error("[rudderstack] SDK failed to load", error);
    }
}

function scheduleLoad(): void {
    if (loadStarted) {
        return;
    }

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        window.requestIdleCallback(() => void loadSdk(), { timeout: 5000 });
    } else {
        setTimeout(() => void loadSdk(), 3000);
    }
}

function onFirstEngagement(): void {
    if (!hasAnalyticsConsentFromCookie()) {
        return;
    }
    scheduleLoad();
}

function subscribeToAxeptioConsent(): void {
    if (typeof window === "undefined") {
        return;
    }

    window._axcb = window._axcb || [];
    window._axcb.push((sdk: unknown) => {
        const typedSdk = sdk as {
            on?: (event: string, cb: (choices: Record<string, unknown>) => void) => void;
        };
        typedSdk.on?.("cookies:complete", (choices) => {
            if (hasAnalyticsConsentFromChoices(choices)) {
                // Cookie may not be written yet; trust the choices payload.
                void loadSdk({ consentGranted: true });
            }
        });
    });
}

/** Bootstrap the deferred, consent-gated RudderStack loader. Idempotent. */
export function initRudderStack(): void {
    if (initialised || getConfig() === null) {
        return;
    }
    initialised = true;

    // Returning visitor who already consented: load on engagement or when idle.
    if (hasAnalyticsConsentFromCookie()) {
        ENGAGEMENT_EVENTS.forEach((name) => {
            window.addEventListener(name, onFirstEngagement, {
                capture: true,
                passive: true,
                once: true,
            });
        });
        scheduleLoad();
    }

    // First consent of the session (or consent change) via Axeptio.
    subscribeToAxeptioConsent();
}

/** Clés de `window.__AB__` (rendu serveur) → propriété envoyée avec chaque page vue et événement. */
const AB_PROPERTIES: Record<string, string> = {
    "exp-202610-marketplace-bar": "ab_marketplace_bar",
    "exp-202611-home-search": "ab_home_search",
};

/**
 * Propriétés communes, lues au moment de l'appel (pas au vidage de la file) : variantes des tests A/B
 * du visiteur (`ab_marketplace_bar: "control" | "test"`), rien hors test.
 */
export function commonProperties(): Record<string, unknown> {
    const ab = typeof window !== "undefined" ? window.__AB__ : undefined;
    const properties: Record<string, unknown> = {};
    if (!ab) {
        return properties;
    }

    Object.entries(AB_PROPERTIES).forEach(([key, property]) => {
        if (typeof ab[key] === "string") {
            properties[property] = ab[key];
        }
    });

    return properties;
}

export function trackEvent(event: string, properties: Record<string, unknown> = {}): void {
    const props = { ...commonProperties(), ...properties };
    enqueue((rs) => rs.track(event, props as ApiObject));
}

export function identifyUser(userId: string, traits: Record<string, unknown> = {}): void {
    enqueue((rs) => rs.identify(userId, traits as IdentifyTraits));
}

export function pageView(): void {
    const props = commonProperties();
    // Surcharge « propriétés seules » du SDK (catégorie et nom laissés vides, comme `rs.page()`)
    enqueue((rs) => rs.page(props as ApiObject));
}
